<template>
  <div
    ref="rootEl"
    class="card-art"
    :class="[
      foilReady ? `foil-${foil}` : 'foil-none',
      `glow-${intensity}`,
      {
        'is-foil': foilReady,
        'foil-live': showFoilFx,
        'foil-still': foilStill,
        'is-face-down': showBack,
      },
    ]"
    :aria-busy="!artReady"
  >
    <div class="card-art-face">
      <span
        v-if="!artReady"
        class="card-art-loader"
        aria-hidden="true"
      >
        <span class="card-art-spinner" />
      </span>
      <img
        v-if="displaySrc"
        :key="shownSrc"
        class="card-art-img"
        :src="shownSrc"
        :alt="alt"
        :loading="eager ? 'eager' : 'lazy'"
        @load="onLoad"
        @error="onError"
      >
      <!--
        Direct siblings of the art (not ::after, not nested). mix-blend-mode
        only composites with the card if these layers do not create their own
        stacking context (no transform / filter / isolation here except the
        Helios burst, which is already premultiplied so it does not need blend).
        Thumbs keep a still wash, and only while near the screen. The sweep
        repaints every frame; a full foil album would do that on every print.
      -->
      <template v-if="showFoilFx">
        <span class="foil-wash" :class="`wash-${foil}`" aria-hidden="true" />
        <template v-if="showFoilMotion">
          <span class="foil-shine" :class="`shine-${foil}`" aria-hidden="true" />
          <span class="foil-fx" :class="`fx-${foil}`" aria-hidden="true" />
          <span
            v-if="foil === 'helios' || foil === 'stormwake' || foil === 'nightglass'"
            class="foil-fx2"
            :class="`fx2-${foil}`"
            aria-hidden="true"
          />
        </template>
      </template>
      <span
        v-if="showFoilName"
        class="foil-name"
        :class="`foil-name-${foilNameKind}`"
        aria-hidden="true"
      >
        <span ref="nameLine" class="foil-name-line">
          <span
            v-for="(part, index) in nameParts"
            :key="index"
            :class="{ sc: part.small }"
          >{{ part.text }}</span>
        </span>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { cardImageCacheQuery, currentSleeveUrl, getLocalCardImageUrl } from '../utils/cardImages'
import { useDuelPrefsStore } from '../stores/duelPrefs'
import { loadCardImage, peekCardImage } from '../utils/cardImageCache'
import { asCardFoil, foilHasOverlay, type CardFoilId } from '@shared/index'
import { englishName } from '../i18n/localizeCard'
import { eraseNameUrl, foilNameKind as foilNameKindOf, foilNameParts } from '../utils/foilName'
import {
  isFoilDebugEnabled,
  publishFoilSnapshot,
  snapshotCardArt,
} from '../utils/foilDebug'

const props = withDefaults(
  defineProps<{
    cardId?: string | number | null
    alt?: string
    faceDown?: boolean
    /**
     * `thumb` is a still tint for album and list grids.
     * `full` runs the sweep, used on the open card.
     */
    intensity?: 'thumb' | 'full'
    /** House print for this copy. Omit to default to Rare. */
    foil?: CardFoilId | string | null
    /**
     * Paint the scan now. Graveyard and Extra lists use this so a missed
     * intersection on the phone does not leave the black plate.
     * Album thumbs stay deferred.
     */
    immediate?: boolean
  }>(),
  {
    cardId: null,
    alt: '',
    faceDown: false,
    intensity: 'full',
    immediate: false,
  },
)

const emit = defineEmits<{
  error: []
}>()

const rootEl = ref<HTMLElement | null>(null)
const displaySrc = ref('')
const painted = ref(false)
const imgError = ref(false)
const attempt = ref(0)
const eager = computed(() => props.intensity === 'full' || props.immediate)
const cardArtRegion = computed(() => useDuelPrefsStore().cardArtRegion)
const motionMinimal = computed(
  () => useDuelPrefsStore().motionLevel === 'minimal',
)

let loadGen = 0
let observer: IntersectionObserver | null = null

watch(
  () => [props.cardId, props.faceDown, cardArtRegion.value] as const,
  () => {
    imgError.value = false
    attempt.value = 0
  },
)

const showBack = computed(
  () => props.faceDown || imgError.value || props.cardId == null
    || props.cardId === '',
)

const foil = computed<CardFoilId | null>(() => {
  if (showBack.value) return null
  if (props.foil === null) return null
  const resolved = asCardFoil(props.foil)
  return foilHasOverlay(resolved) ? resolved : null
})

const printedName = computed(() => englishName(props.cardId ?? ''))
const foilNameKind = computed(() => foilNameKindOf(foil.value))
const nameParts = computed(() => foilNameParts(printedName.value))
const plateSrc = ref('')
const platePainted = ref(false)
const shownSrc = computed(() => plateSrc.value || displaySrc.value)
const showFoilName = computed(
  () => !!foilNameKind.value && platePainted.value && printedName.value !== '',
)
const nameLine = ref<HTMLElement | null>(null)

const artReady = computed(() => painted.value && displaySrc.value !== '')
const foilReady = computed(() => foil.value != null && artReady.value)
/** Settings can turn the sweep back on for album and list thumbs. */
const animateFoilThumbs = computed(
  () => useDuelPrefsStore().animateFoilThumbs,
)
/** Full-size art always. Still thumbs only while the slot is near the screen. */
const foilOnScreen = ref(
  props.intensity === 'full' || animateFoilThumbs.value,
)
const showFoilFx = computed(
  () => foilReady.value && !motionMinimal.value && foilOnScreen.value,
)
const showFoilMotion = computed(
  () => showFoilFx.value
    && (props.intensity === 'full' || animateFoilThumbs.value),
)
const foilStill = computed(
  () => props.intensity !== 'full' && !animateFoilThumbs.value,
)

const src = computed(() => {
  if (showBack.value) return currentSleeveUrl()
  return getLocalCardImageUrl(props.cardId as string | number)
})

const onLoad = () => {
  painted.value = true
  platePainted.value = plateSrc.value !== '' && shownSrc.value === plateSrc.value
}

function artCandidates(): string[] {
  const id = String(props.cardId)
  const rev = cardImageCacheQuery(id)
  const tcg = `/card-images/tcg/${id}.jpg${rev}`
  const scan = `/card-images/${id}.jpg${rev}`
  const preferred = cardArtRegion.value === 'tcg' ? tcg : scan
  const other = preferred === tcg ? scan : tcg
  return [preferred, other, `${preferred}?r=1`]
}

const onError = (event: Event) => {
  const failed = event.target instanceof HTMLImageElement ? event.target.src : ''
  if (failed.startsWith('data:')) {
    plateSrc.value = ''
    return
  }
  if (imgError.value || props.faceDown || props.cardId == null || props.cardId === '') {
    if (imgError.value) return
    imgError.value = true
    emit('error')
    return
  }
  const next = artCandidates()[attempt.value + 1]
  if (next && next !== displaySrc.value) {
    attempt.value += 1
    displaySrc.value = next
    return
  }
  imgError.value = true
  emit('error')
}

function stopObserver(): void {
  observer?.disconnect()
  observer = null
}

let foilObserver: IntersectionObserver | null = null

function stopFoilObserver(): void {
  foilObserver?.disconnect()
  foilObserver = null
}

/**
 * A thumb grid mounts one card per print. Keep the blend wash on the
 * slots near the screen and drop it everywhere else.
 */
function syncFoilObserver(): void {
  stopFoilObserver()
  if (props.intensity === 'full' || animateFoilThumbs.value) {
    foilOnScreen.value = true
    return
  }
  const el = rootEl.value
  if (!el || typeof IntersectionObserver === 'undefined') {
    foilOnScreen.value = true
    return
  }
  foilOnScreen.value = false
  foilObserver = new IntersectionObserver(
    (entries) => {
      foilOnScreen.value = entries.some((entry) => entry.isIntersecting)
    },
    { rootMargin: '160px 0px', threshold: 0 },
  )
  foilObserver.observe(el)
}

/**
 * Point <img> at the HTTP URL immediately so the browser can paint from
 * disk cache. loadCardImage still warms the shared decode for Phaser/Three.
 * mix-blend foil needs an http(s) src, not a blob:.
 */
function showUrl(url: string, gen: number, ready: boolean): void {
  if (gen !== loadGen) return
  displaySrc.value = url
  if (ready) painted.value = true
}

function beginLoad(url: string, gen: number): void {
  void loadCardImage(url).then(() => {
    if (gen !== loadGen) return
    painted.value = true
  }).catch(() => {
    showUrl(url, gen, false)
  })
}

function syncImage(): void {
  stopObserver()
  const gen = ++loadGen
  painted.value = false
  const url = src.value
  const peeked = peekCardImage(url)
  if (peeked) {
    showUrl(url, gen, true)
    return
  }
  if (eager.value) {
    // Start the <img> GET now — do not wait for the shared decoder.
    showUrl(url, gen, false)
    beginLoad(url, gen)
    return
  }
  displaySrc.value = ''
  const el = rootEl.value
  if (!el) return
  observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      stopObserver()
      showUrl(url, gen, false)
      beginLoad(url, gen)
    },
    { rootMargin: '160px 0px', threshold: 0.01 },
  )
  observer.observe(el)
}

let nameObserver: ResizeObserver | null = null
let plateGen = 0

function fitName(): void {
  const line = nameLine.value
  const card = rootEl.value
  if (!line || !card) return
  const h = card.clientHeight
  if (h < 8) return
  line.style.fontSize = `${h * 0.0526}px`
  line.style.transform = 'none'
  const box = line.parentElement
  if (!box) return
  const available = box.clientWidth
  if (available > 0 && line.scrollWidth > available + 0.5) {
    line.style.transform = `scaleX(${available / line.scrollWidth})`
  }
}

function syncNameFit(): void {
  nameObserver?.disconnect()
  nameObserver = null
  const el = rootEl.value
  if (!el || !showFoilName.value) return
  if (typeof ResizeObserver !== 'undefined') {
    nameObserver = new ResizeObserver(() => fitName())
    nameObserver.observe(el)
  }
  void nextTick(() => fitName())
  void document.fonts?.ready.then(() => fitName())
}

function queueNamePlate(): void {
  const gen = ++plateGen
  plateSrc.value = ''
  platePainted.value = false
  const url = displaySrc.value
  // Thumbs stay on the HTTP scan. Replacing every Rare / Ultra / Ghost
  // in a grid with a canvas JPEG paints black on iPhone Safari; the full
  // card is one plate and still reprints the name.
  if (props.intensity !== 'full') return
  if (!foilNameKind.value || !printedName.value || !url || url.startsWith('data:')) return
  void eraseNameUrl(url).then((next) => {
    if (gen !== plateGen) return
    if (next.startsWith('data:')) plateSrc.value = next
  })
}

watch([src, eager], syncImage)
watch(displaySrc, queueNamePlate)
watch(foilNameKind, queueNamePlate)
watch(() => props.intensity, queueNamePlate)
watch(showFoilName, syncNameFit)
watch(() => props.intensity, () => {
  if (!rootEl.value) return
  syncFoilObserver()
})
watch(animateFoilThumbs, () => {
  if (!rootEl.value) return
  syncFoilObserver()
})
onMounted(() => {
  syncImage()
  syncFoilObserver()
  syncNameFit()
})
onUnmounted(() => {
  loadGen += 1
  plateGen += 1
  stopObserver()
  stopFoilObserver()
  nameObserver?.disconnect()
  nameObserver = null
})

function reportFoilDebug(): void {
  if (!isFoilDebugEnabled() || props.intensity !== 'full') return
  const el = rootEl.value
  if (!el || !foilReady.value) return
  publishFoilSnapshot(snapshotCardArt(el, {
    foilProp: String(props.foil ?? ''),
    foilResolved: foil.value,
    foilReady: foilReady.value,
    alt: props.alt,
  }))
}

watch(
  [foilReady, displaySrc],
  () => {
    if (!foilReady.value) return
    void nextTick(() => {
      requestAnimationFrame(() => reportFoilDebug())
    })
  },
)
</script>

<style scoped>
.card-art {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 4.5% / 3.2%;
  overflow: hidden;
  isolation: isolate;
  /* Opaque mask so mix-blend foil cannot paint past the rounded rect. */
  -webkit-mask-image: linear-gradient(#fff, #fff);
  mask-image: linear-gradient(#fff, #fff);
  clip-path: inset(0 round 4.5% / 3.2%);
  transform-style: flat;
  forced-color-adjust: none;
}

.card-art-face {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: inherit;
  clip-path: inset(0 round 4.5% / 3.2%);
  background: #131316;
  isolation: isolate;
  transform-style: flat;
}

/* Offscreen thumbs are a plain picture. The mask exists so a blend
   layer cannot paint past the rounded rect. */
.card-art.glow-thumb:not(.foil-live),
.card-art.glow-thumb:not(.foil-live) .card-art-face {
  -webkit-mask-image: none;
  mask-image: none;
  clip-path: none;
  isolation: auto;
}

.card-art-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.card-art-loader {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #131316;
}

.card-art-spinner {
  width: 28%;
  max-width: 22px;
  aspect-ratio: 1;
  border: 2px solid rgba(255, 255, 255, 0.16);
  border-top-color: var(--md-sys-color-primary, #fbbf24);
  border-radius: 50%;
  animation: ygo-card-art-spin 0.7s linear infinite;
}

.foil-wash,
.foil-shine,
.foil-fx,
.foil-fx2 {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  border-radius: inherit;
}

/* Rare — always a faint silver veil; shine sweeps on top. */
.wash-rare {
  box-shadow: inset 0 0 10px 1px rgba(226, 232, 240, 0.35);
  background: linear-gradient(
    125deg,
    rgba(226, 232, 240, 0.12),
    transparent 42%,
    rgba(255, 255, 255, 0.08)
  );
}

.shine-rare,
.shine-super,
.shine-ultra,
.shine-helios,
.shine-nightglass,
.shine-stormwake {
  background: linear-gradient(
    105deg,
    transparent 36%,
    rgba(226, 232, 240, 0.4) 46%,
    rgba(255, 255, 255, 0.88) 50%,
    rgba(226, 232, 240, 0.4) 54%,
    transparent 64%
  );
  background-size: 220% 100%;
  background-position: 50% 0;
  mix-blend-mode: screen;
  animation: ygo-foil-sweep 7.5s linear infinite;
}

.shine-super {
  background: linear-gradient(
    105deg,
    transparent 38%,
    rgba(250, 204, 21, 0.38) 47%,
    rgba(254, 249, 195, 0.8) 50%,
    rgba(163, 230, 53, 0.28) 53%,
    transparent 66%
  );
  background-size: 220% 100%;
}

.shine-ultra {
  background: linear-gradient(
    100deg,
    transparent 12%,
    rgba(180, 83, 9, 0.55) 32%,
    rgba(251, 191, 36, 0.85) 46%,
    rgba(255, 251, 220, 1) 50%,
    rgba(245, 158, 11, 0.8) 54%,
    rgba(146, 64, 14, 0.45) 70%,
    transparent 88%
  );
  background-size: 280% 100%;
  mix-blend-mode: screen;
  animation: ygo-foil-sweep 5.8s linear infinite;
}

.shine-helios {
  background: linear-gradient(
    112deg,
    transparent 18%,
    rgba(255, 210, 80, 0.45) 38%,
    rgba(255, 250, 220, 0.95) 50%,
    rgba(255, 140, 30, 0.55) 58%,
    transparent 78%
  );
  background-size: 240% 100%;
  mix-blend-mode: screen;
}

.shine-nightglass {
  background: linear-gradient(
    100deg,
    transparent 12%,
    rgba(56, 189, 248, 0.7) 32%,
    rgba(196, 181, 253, 0.85) 44%,
    rgba(255, 255, 255, 1) 50%,
    rgba(244, 114, 182, 0.75) 56%,
    rgba(34, 211, 238, 0.55) 70%,
    transparent 88%
  );
  background-size: 280% 100%;
  mix-blend-mode: screen;
  animation:
    ygo-foil-pulse 2.6s ease-in-out infinite,
    ygo-foil-sweep 5.4s linear infinite;
}

.shine-stormwake {
  background: linear-gradient(
    108deg,
    transparent 14%,
    rgba(244, 114, 182, 0.7) 36%,
    rgba(255, 255, 255, 1) 50%,
    rgba(34, 211, 238, 0.75) 64%,
    transparent 86%
  );
  background-size: 260% 100%;
  mix-blend-mode: screen;
  animation: ygo-foil-sweep 5.2s linear infinite;
}

.wash-ghost {
  background:
    radial-gradient(ellipse at 50% 40%, rgba(255, 255, 255, 0.35), transparent 62%),
    linear-gradient(160deg, rgba(226, 232, 240, 0.28), rgba(148, 163, 184, 0.12));
  mix-blend-mode: screen;
}

.shine-ghost {
  background: linear-gradient(
    102deg,
    transparent 36%,
    rgba(255, 255, 255, 0.12) 46%,
    rgba(224, 242, 254, 0.45) 50%,
    rgba(221, 214, 254, 0.2) 54%,
    transparent 64%
  );
  background-size: 220% 100%;
  background-position: 50% 0;
  mix-blend-mode: screen;
  animation: ygo-foil-sweep 6.8s linear infinite;
}

.fx-ghost {
  background: repeating-linear-gradient(
    180deg,
    transparent 0 10px,
    rgba(255, 255, 255, 0.16) 11px,
    transparent 12px 22px
  );
  mix-blend-mode: screen;
  animation: ygo-foil-band 7s linear infinite;
}

/* Super Rare — lime/amber holo, not solid gold. */
.wash-super {
  background: linear-gradient(
    125deg,
    rgba(250, 204, 21, 0.28),
    rgba(163, 230, 53, 0.22),
    rgba(251, 146, 60, 0.24)
  );
  background-size: 220% 220%;
  mix-blend-mode: lighten;
  animation: ygo-foil-drift 4.4s ease-in-out infinite;
  box-shadow: inset 0 0 10px 1px rgba(250, 204, 21, 0.22);
}

/* Ultra Rare — stamped gold metal, distinct from Super's holo. */
.wash-ultra {
  background:
    linear-gradient(
      168deg,
      rgba(146, 64, 14, 0.55),
      rgba(251, 191, 36, 0.5) 34%,
      rgba(255, 247, 200, 0.32) 50%,
      rgba(180, 83, 9, 0.52)
    );
  mix-blend-mode: lighten;
  box-shadow:
    inset 0 0 12px 3px rgba(251, 191, 36, 0.8),
    inset 0 0 28px 6px rgba(146, 64, 14, 0.45);
}

.fx-ultra {
  background: repeating-linear-gradient(
    92deg,
    transparent 0 8px,
    rgba(255, 215, 90, 0.28) 9px,
    rgba(255, 247, 220, 0.16) 11px,
    transparent 13px 20px
  );
  mix-blend-mode: overlay;
  animation: ygo-foil-slide 6.4s linear infinite;
}

/* Helios — molten gold wash + spinning sunburst. */
.wash-helios {
  background: radial-gradient(
    ellipse at 50% 42%,
    rgba(255, 247, 220, 0.42),
    rgba(251, 191, 36, 0.28) 38%,
    rgba(180, 80, 0, 0.12) 72%,
    transparent 78%
  );
  mix-blend-mode: lighten;
  box-shadow: inset 0 0 22px 5px rgba(255, 170, 40, 0.7);
  animation: ygo-foil-pulse 2.8s ease-in-out infinite;
}

.fx-helios {
  inset: -55%;
  background: repeating-conic-gradient(
    from 0deg at 50% 48%,
    rgba(255, 220, 90, 0) 0deg 9deg,
    rgba(255, 190, 50, 0.62) 10deg,
    rgba(255, 255, 220, 0.42) 12deg,
    rgba(255, 140, 20, 0.28) 14deg,
    rgba(255, 220, 90, 0) 16deg
  );
  mix-blend-mode: lighten;
  animation: ygo-foil-spin 26s linear infinite;
}

.fx2-helios {
  background: radial-gradient(
    circle at 50% 46%,
    rgba(255, 236, 160, 0.5),
    rgba(255, 160, 40, 0.18) 36%,
    transparent 62%
  );
  mix-blend-mode: screen;
  animation: ygo-foil-pulse 2.2s ease-in-out infinite;
}

/* Nightglass — icy prism glass, a step above Ultra's gold stamp. */
.wash-nightglass {
  background:
    radial-gradient(
      ellipse at 18% 22%,
      rgba(186, 230, 253, 0.7),
      transparent 48%
    ),
    radial-gradient(
      ellipse at 82% 78%,
      rgba(167, 139, 250, 0.62),
      transparent 50%
    ),
    linear-gradient(
      125deg,
      rgba(56, 189, 248, 0.48),
      rgba(167, 139, 250, 0.42) 38%,
      rgba(244, 114, 182, 0.32) 62%,
      rgba(34, 211, 238, 0.5)
    );
  background-size: 100% 100%, 100% 100%, 280% 280%;
  mix-blend-mode: lighten;
  animation: ygo-foil-drift 3.8s ease-in-out infinite;
  box-shadow:
    inset 0 0 16px 4px rgba(125, 211, 252, 0.85),
    inset 0 0 32px 8px rgba(139, 92, 246, 0.45);
}

.fx-nightglass {
  background: repeating-linear-gradient(
    118deg,
    transparent 0 6px,
    rgba(224, 242, 254, 0.42) 7px,
    rgba(167, 139, 250, 0.28) 9px,
    rgba(244, 114, 182, 0.18) 11px,
    transparent 13px 18px
  );
  mix-blend-mode: screen;
  animation: ygo-foil-slide 4.6s linear infinite;
}

.fx2-nightglass {
  background:
    radial-gradient(
      ellipse at 70% 24%,
      rgba(255, 255, 255, 0.42),
      transparent 38%
    ),
    radial-gradient(
      ellipse at 28% 76%,
      rgba(34, 211, 238, 0.4),
      transparent 44%
    );
  mix-blend-mode: screen;
  animation: ygo-foil-pulse 2.4s ease-in-out infinite;
}

/* Stormwake — neon plasma + dense falling stardust. */
.wash-stormwake {
  background:
    radial-gradient(
      ellipse at 22% 14%,
      rgba(255, 90, 180, 0.78),
      transparent 46%
    ),
    radial-gradient(
      ellipse at 82% 78%,
      rgba(20, 230, 255, 0.72),
      transparent 48%
    ),
    radial-gradient(
      ellipse at 50% 46%,
      rgba(255, 255, 255, 0.38),
      transparent 54%
    );
  mix-blend-mode: lighten;
  animation: ygo-foil-drift 3.6s ease-in-out infinite;
  box-shadow:
    inset 0 0 18px 5px rgba(244, 114, 182, 0.75),
    inset 0 0 32px 8px rgba(34, 211, 238, 0.4);
}

.fx-stormwake,
.fx2-stormwake {
  left: 0;
  right: 0;
  height: 170%;
  mix-blend-mode: screen;
  --core: 2.4px;
  --mid: 4px;
  --halo: 7.5px;
}

.card-art.glow-full .fx-stormwake,
.card-art.glow-full .fx2-stormwake {
  --core: 3.6px;
  --mid: 6px;
  --halo: 11px;
}

.fx-stormwake {
  top: -40%;
  bottom: auto;
  background-image:
    radial-gradient(
      circle at 8% 10%, #fff 0 var(--core), #f9a8d4 var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 19% 24%, #fff 0 var(--core), #67e8f9 var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 33% 8%, #fff 0 var(--core), #f472b6 var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 47% 31%, #fff 0 var(--core), #a5f3fc var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 61% 14%, #fff 0 var(--core), #f9a8d4 var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 74% 27%, #fff 0 var(--core), #22d3ee var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 88% 9%, #fff 0 var(--core), #fbcfe8 var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 12% 48%, #fff 0 var(--core), #67e8f9 var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 29% 61%, #fff 0 var(--core), #f472b6 var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 43% 52%, #fff 0 var(--core), #a5f3fc var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 58% 68%, #fff 0 var(--core), #f9a8d4 var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 71% 55%, #fff 0 var(--core), #67e8f9 var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 86% 63%, #fff 0 var(--core), #22d3ee var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 6% 82%, #fff 0 var(--core), #f472b6 var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 24% 88%, #fff 0 var(--core), #fbcfe8 var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 51% 84%, #fff 0 var(--core), #a5f3fc var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 79% 91%, #fff 0 var(--core), #f9a8d4 var(--mid),
      transparent var(--halo)
    );
  animation:
    ygo-foil-fall 4.4s linear infinite,
    ygo-foil-twinkle 1.7s ease-in-out infinite;
}

.fx2-stormwake {
  top: -20%;
  bottom: auto;
  background-image:
    radial-gradient(
      circle at 14% 18%, #fff 0 var(--core), #67e8f9 var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 37% 6%, #fff 0 var(--core), #f9a8d4 var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 54% 22%, #fff 0 var(--core), #22d3ee var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 69% 11%, #fff 0 var(--core), #f472b6 var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 91% 29%, #fff 0 var(--core), #fff var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 21% 44%, #fff 0 var(--core), #a5f3fc var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 41% 57%, #fff 0 var(--core), #f9a8d4 var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 63% 41%, #fff 0 var(--core), #67e8f9 var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 82% 53%, #fff 0 var(--core), #f472b6 var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 9% 71%, #fff 0 var(--core), #fff var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 35% 79%, #fff 0 var(--core), #22d3ee var(--mid),
      transparent var(--halo)
    ),
    radial-gradient(
      circle at 72% 86%, #fff 0 var(--core), #fbcfe8 var(--mid),
      transparent var(--halo)
    );
  animation:
    ygo-foil-fall 5.1s linear infinite reverse,
    ygo-foil-twinkle 2.15s ease-in-out infinite reverse;
}

.card-art.is-foil.glow-full.foil-rare {
  box-shadow: 0 0 10px 2px rgba(203, 213, 225, 0.5);
}

.card-art.is-foil.glow-full.foil-super {
  box-shadow: 0 0 10px 2px rgba(250, 204, 21, 0.42);
}

.card-art.is-foil.glow-full.foil-ultra {
  box-shadow:
    0 0 14px 3px rgba(251, 191, 36, 0.92),
    0 0 28px 7px rgba(180, 83, 9, 0.5);
}

.card-art.is-foil.glow-full.foil-ghost {
  box-shadow: 0 0 18px 4px rgba(226, 232, 240, 0.7);
}

.card-art.is-foil.glow-full.foil-helios {
  box-shadow:
    0 0 18px 4px rgba(251, 191, 36, 0.85),
    0 0 36px 8px rgba(255, 140, 20, 0.5);
}

.card-art.is-foil.glow-full.foil-nightglass {
  box-shadow:
    0 0 18px 4px rgba(125, 211, 252, 0.95),
    0 0 36px 9px rgba(139, 92, 246, 0.55);
}

.card-art.is-foil.glow-full.foil-stormwake {
  box-shadow:
    0 0 20px 5px rgba(244, 114, 182, 0.95),
    0 0 36px 10px rgba(34, 211, 238, 0.65);
}

.card-art.is-foil.glow-thumb.foil-rare {
  box-shadow: 0 0 6px 1px rgba(203, 213, 225, 0.45);
}

.card-art.is-foil.glow-thumb.foil-super {
  box-shadow: 0 0 6px 1px rgba(250, 204, 21, 0.4);
}

.card-art.is-foil.glow-thumb.foil-ultra {
  box-shadow: 0 0 10px 3px rgba(251, 191, 36, 0.78);
}

.card-art.is-foil.glow-thumb.foil-ghost {
  box-shadow: 0 0 8px 2px rgba(226, 232, 240, 0.65);
}

.card-art.is-foil.glow-thumb.foil-helios {
  box-shadow: 0 0 10px 3px rgba(251, 191, 36, 0.78);
}

.card-art.is-foil.glow-thumb.foil-nightglass {
  box-shadow:
    0 0 10px 3px rgba(125, 211, 252, 0.85),
    0 0 16px 4px rgba(139, 92, 246, 0.45);
}

.card-art.is-foil.glow-thumb.foil-stormwake {
  box-shadow:
    0 0 12px 3px rgba(244, 114, 182, 0.9),
    0 0 18px 4px rgba(34, 211, 238, 0.5);
}

/* Still tint. The sweep on every album print repaints the whole grid. */
.card-art.foil-still .foil-wash {
  animation: none;
}

@media (prefers-reduced-motion: reduce) {
  .foil-wash,
  .foil-shine,
  .foil-fx,
  .foil-fx2,
  .foil-name-line {
    animation: none;
  }

  .foil-shine {
    background-position: 50% 0;
  }

  .card-art-spinner {
    animation: none;
    opacity: 0.55;
  }
}
</style>

<!--
  Unscoped so Vite/esbuild minify cannot desync hashed @keyframes from
  animation-name (that break is silent in `vite build` / Docker deploys).
-->
<style>
@font-face {
  font-family: "Card Name";
  src: url("/card-name/DejaVuSerifCondensed.ttf") format("truetype");
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

.foil-name {
  position: absolute;
  z-index: 4;
  left: 7.5%;
  top: 4.85%;
  width: 74%;
  /* Taller than the cap line so a J hook is not clipped. */
  height: 6.8%;
  display: flex;
  align-items: flex-start;
  overflow: visible;
  pointer-events: none;
}

.foil-name-line {
  display: inline-block;
  font-family: "Card Name", "Times New Roman", serif;
  font-weight: 400;
  font-synthesis: none;
  line-height: 1;
  letter-spacing: 0;
  white-space: pre;
  transform-origin: left center;
  background-size: 220% 100%;
  background-position: 0 0;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.foil-name .sc {
  font-size: 0.76em;
}

.foil-name-silver .foil-name-line {
  background-image: linear-gradient(100deg, #6b7280 0%, #f8fafc 42%, #9ca3af 58%, #e5e7eb 100%);
  animation: ygo-foil-sweep 3.6s linear infinite;
}

.foil-name-ultra .foil-name-line {
  background-image: linear-gradient(100deg, #fff7d6 0%, #ffffff 28%, #ffe9a0 46%, #fffdf5 62%, #f3d27a 100%);
  animation: ygo-foil-sweep 4.8s linear infinite;
}

.foil-name-ghost .foil-name-line {
  background-image: linear-gradient(100deg, #64748b 0%, #ffffff 18%, #cbd5e1 36%, #f8fafc 50%, #94a3b8 68%, #ffffff 84%, #64748b 100%);
  animation: ygo-foil-sweep 5.4s linear infinite;
}

/* Vue CardArt. Nítida bakes this wash into the JPEG; `is-ghost` is only
   the preview before that plate lands (and a fallback if the bake fails). */
.card-art.foil-ghost .card-art-img,
img.sharp-card-img.is-ghost {
  filter: grayscale(0.88) brightness(1.42) contrast(0.76) sepia(0.08);
}

.card-art.foil-still .foil-name-line,
.card-art:not(.foil-live) .foil-name-line {
  animation: none;
}

@supports (mix-blend-mode: plus-lighter) {
  .card-art .wash-super,
  .card-art .wash-ultra,
  .card-art .wash-ghost,
  .card-art .wash-helios,
  .card-art .fx-helios,
  .card-art .wash-nightglass,
  .card-art .fx2-nightglass,
  .card-art .wash-stormwake {
    mix-blend-mode: plus-lighter;
  }
}

@keyframes ygo-card-art-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes ygo-foil-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes ygo-foil-sweep {
  0% {
    background-position: 120% 0;
  }
  100% {
    background-position: -20% 0;
  }
}

@keyframes ygo-foil-band {
  from { background-position: 0 0; }
  to { background-position: 0 40px; }
}

@keyframes ygo-foil-drift {
  0%,
  100% {
    background-position: 0% 40%;
  }
  50% {
    background-position: 100% 70%;
  }
}

@keyframes ygo-foil-slide {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: -28px 18px;
  }
}

@keyframes ygo-foil-pulse {
  0%,
  100% {
    opacity: 0.45;
  }
  50% {
    opacity: 1;
  }
}

@keyframes ygo-foil-fall {
  0% {
    transform: translateY(-10%);
  }
  100% {
    transform: translateY(10%);
  }
}

@keyframes ygo-foil-twinkle {
  0%,
  14% {
    opacity: 0.08;
  }
  22%,
  34% {
    opacity: 1;
  }
  42%,
  52% {
    opacity: 0.06;
  }
  60%,
  74% {
    opacity: 1;
  }
  84%,
  100% {
    opacity: 0.12;
  }
}
</style>
